import { useTranslation } from 'react-i18next';

import { NumberCell } from '@work4all/components';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import {
  OpenMaskOverride,
  useOpenMask,
} from '../../../containers/mask-overlays/mask-overlay/hooks/use-open-mask';
import { useStringifyTableGroupFilter } from '../../../hooks';
import { OrganisationArea } from '../../organisation-area';
import { AttachmentsCell } from '../article-table/cells/AttachmentsCell';
import { StockArticleCell } from '../article-table/cells/StockArticleCell';
import { EntityTableV8, IEntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import schema from '../schemata/article-table-schema.json';

import { useTransformedArticleTableSchema } from './use-transformed-article-table-schema';

const defaultSort = [
  { field: 'creationDate', direction: SortDirection.DESCENDING },
];

const secondarySort = [
  { field: 'number', direction: SortDirection.DESCENDING },
];

const forceRequestFields: Article = {
  groupId: null,
  netPrice: null,
  netPrice2: null,
  netPrice3: null,
  hasBulkPrices: null,
  bulkPriceList: [
    {
      id: null,
      sdObjMemberCode: null,
      price: null,
      bulkPriceTierItem: {
        id: null,
        name: null,
        edgeValue1: null,
        edgeValue2: null,
      },
      priceGroupId: null,
    },
  ],
  articleTextList: [
    {
      id: null,
      kurztext: null,
      longtext: null,
      sprachCode: null,
    },
  ],
  articlePrices: {
    singlePriceList: [
      {
        id: null,
        price: null,
        priceGroupId: null,
      },
    ],
    purchasePriceList: [
      {
        id: null,
        purchasePrice: null,
        amountPerUnit: null,
        supplier: {
          id: null,
          name: null,
        },
        currency: { id: null, name: null },
        validFromDate: null,
      },
    ],
  },
};

export function ServiceContractArticleTable(props: IEntityTableV8) {
  const { t } = useTranslation();

  const transformedSchema = useTransformedArticleTableSchema({
    schema,
    source: 'table',
  });

  const dataTable = useEntityDataTableV8<Article, never>({
    ...props,
    schema: transformedSchema as never,
    defaultSort,
    secondarySort,
    forceRequestFields,
    cells: {
      AttachmentsCell,
      NumberCell,
      StockArticleCell,
    },
    rowModifiers: (value) => {
      return {
        isFaded: value.isShutDown,
      };
    },
  });

  const presetFields = useStringifyTableGroupFilter<Article>({
    groupKey: 'articleGroup',
    groupIdKey: 'groupId',
    table: dataTable.table,
  });

  const onOpenMask = useOpenMask({
    entityType: Entities.article,
    params: { create: { presetFields } },
  });

  return (
    <OpenMaskOverride value={{ onOpenMask }}>
      <EntityTableV8
        {...props}
        {...dataTable}
        areas={{
          left: {
            title: t('COMMON.ORGANISATION'),
            content: (
              <OrganisationArea
                entity={Entities.article}
                table={dataTable.table}
              />
            ),
            resizable: true,
            collapsible: true,
            custom: true,
          },
        }}
        actions={{
          createWidget: false,
        }}
      />
    </OpenMaskOverride>
  );
}
